import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../App/redux/store';
import { APIStatus } from '../../services/axiosEnums';
import {
  askforPasswordResetLinkThunk,
  fetchAuthUserThunk,
  loginThunk,
  logoutThunk,
  passwordChangeThunk,
  resetOrCreatePasswordThunk,
  updateAuthUserThunk,
} from './services/authThunks';

const initialState: AuthState = {
  token: null,
  refreshToken: null,
  isLogged: false,
  isManager: false,
  isAdmin: false,
  login: { apiStatus: APIStatus.IDLE, result: false, error: undefined },
  user: { apiStatus: APIStatus.IDLE, result: null, error: undefined },
  userIdIri: null,
  maintenancePageOnly: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: () => ({ ...initialState }),
    setTokens: (
      state,
      action: PayloadAction<Pick<AuthState, 'token' | 'refreshToken' | 'userIdIri'>>,
    ) => {
      const ap = action.payload;
      state.token = ap.token;
      state.refreshToken = ap.refreshToken;
      state.userIdIri = ap.userIdIri;
    },
    refreshTokenSet: (
      state,
      action: PayloadAction<Pick<AuthState, 'token' | 'refreshToken'>>,
    ) => {
      const ap = action.payload;
      state.token = ap.token;
      state.refreshToken = ap.refreshToken;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.login.apiStatus = APIStatus.PENDING;
        state.login.error = undefined;
      })
      .addCase(
        loginThunk.fulfilled,
        (state, action: PayloadAction<LoginResponseType>) => {
          const ap = action.payload;
          state.userIdIri = ap.userIdIri;
          state.token = ap.token;
          state.refreshToken = ap.refreshToken;

          state.login.apiStatus = APIStatus.IDLE;
          state.login.result = true;
          state.login.error = undefined;
        },
      )
      .addCase(loginThunk.rejected, (state, action) => {
        state.login.apiStatus = APIStatus.REJECTED;
        state.login.error = action.error;
      });
    builder
      .addCase(logoutThunk.pending, (state) => {})
      .addCase(logoutThunk.fulfilled, (state) => {})
      .addCase(logoutThunk.rejected, (state) => {});
    builder
      .addCase(fetchAuthUserThunk.pending, (state) => {
        state.user.apiStatus = APIStatus.PENDING;
        state.user.error = undefined;
        state.user.result = null;
      })
      .addCase(
        fetchAuthUserThunk.fulfilled,
        (state, action: PayloadAction<IUser>) => {
          state.user.apiStatus = APIStatus.IDLE;
          state.user.result = action.payload;
          state.isLogged = true;
          state.isManager = action.payload.isManager;
          state.isAdmin = action.payload.isAdmin;
          state.user.error = undefined;
        },
      )
      .addCase(fetchAuthUserThunk.rejected, (state, action) => {
        state.user.apiStatus = APIStatus.REJECTED;
        state.user.error = action.error;
        state.user.result = null;
      });
    builder
      .addCase(updateAuthUserThunk.pending, (state) => {
        state.user.apiStatus = APIStatus.PENDING;
        state.user.error = undefined;
      })
      .addCase(
        updateAuthUserThunk.fulfilled,
        (state, action: PayloadAction<IUser>) => {
          state.user.apiStatus = APIStatus.IDLE;
          state.user.error = undefined;
          state.user.result = { ...(state.user.result as IUser), ...action.payload };
        },
      )
      .addCase(updateAuthUserThunk.rejected, (state, action) => {
        state.user.apiStatus = APIStatus.REJECTED;
        state.user.error = action.error;
      });
    builder
      .addCase(passwordChangeThunk.pending, (state) => {
        state.user.apiStatus = APIStatus.PENDING;
        state.user.error = undefined;
      })
      .addCase(passwordChangeThunk.fulfilled, (state) => {
        state.user.apiStatus = APIStatus.IDLE;
        state.user.error = undefined;
      })
      .addCase(passwordChangeThunk.rejected, (state, action) => {
        state.user.apiStatus = APIStatus.REJECTED;
        state.user.error = action.error;
      });
    builder
      .addCase(askforPasswordResetLinkThunk.pending, (state) => {
        state.user.apiStatus = APIStatus.PENDING;
        state.user.error = undefined;
      })
      .addCase(askforPasswordResetLinkThunk.fulfilled, (state) => {
        state.user.apiStatus = APIStatus.IDLE;
        state.user.error = undefined;
      })
      .addCase(askforPasswordResetLinkThunk.rejected, (state, action) => {
        state.user.apiStatus = APIStatus.REJECTED;
        state.user.error = action.error;
      });
    builder
      .addCase(resetOrCreatePasswordThunk.pending, (state) => {
        state.user.apiStatus = APIStatus.PENDING;
        state.user.error = undefined;
      })
      .addCase(resetOrCreatePasswordThunk.fulfilled, (state) => {
        state.user.apiStatus = APIStatus.IDLE;
        state.user.error = undefined;
      })
      .addCase(resetOrCreatePasswordThunk.rejected, (state, action) => {
        state.user.apiStatus = APIStatus.REJECTED;
        state.user.error = action.error;
      });
  },
});

export const resetAppAction = createAction('app/resetAll');

export default authSlice.reducer;
export const authActions = authSlice.actions;
export const getAuthState = (state: RootState) => state.auth;
